import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useFetchTrigger } from '../../lib/useFetchTrigger';
import type { Holiday } from './types';
import { sameWindow } from '../bookings/domain';
import type { DateWindow } from '../bookings/types';

// Staff leave for a date window (the Holidays views + the roster's leave
// awareness read their window through this). Writes go straight to the API from
// the log/edit dialog; reload() refetches after a write. Holds the last-known
// set across a window change so the view never blanks, and revalidates on return.
// Composes the shared SWR trigger core (lib/useFetchTrigger) — replacing a
// drifted `nonce > 0 ? { cache: 'reload' } : {}` under which one reload() made
// every later fetch bypass the cache forever while tick revalidations never did.
// `window` echoes the range `holidays` are actually FOR (null before the first
// load), and `failed` marks a window whose fetch did not land. Both exist for
// the same reason as their useShifts counterparts: the set is HELD across a
// window change, so a consumer that draws a conclusion from it — a week that
// looks unstaffed, a day that looks free of leave — cannot otherwise tell the
// held set from the requested one (PR #164, codex P2).
export function useHolidays(
  from: string,
  to: string,
): { holidays: Holiday[]; window: DateWindow | null; failed: boolean; reload: () => void } {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [window_, setWindow] = useState<DateWindow | null>(null);
  // The window whose fetch FAILED, not a bare flag. A boolean set on failure
  // is only ever cleared by a later success, so one bad week made every week
  // stepped to afterwards raise a failure banner instantly — before its own
  // request had said anything (PR #164, claude P2). Scoping the failure to the
  // window it happened in makes it expire on its own, the same way `window`
  // above tells a consumer which set it is holding.
  const [failedFor, setFailedFor] = useState<DateWindow | null>(null);
  const trigger = useFetchTrigger();

  useEffect(() => {
    const run = trigger.begin();
    api<Holiday[]>(`/holidays?from=${from}&to=${to}`, run.init)
      .then((body) => {
        if (!run.isCurrent()) return; // superseded
        run.markContent();
        setHolidays(body.data ?? []);
        setWindow({ from, to });
        setFailedFor(null);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) return;
        // hold the last-known set on a failed fetch, and mark it as held
        if (!run.isCurrent()) return;
        setFailedFor({ from, to });
      });
    return run.cancel;
  }, [from, to, trigger.nonce, trigger.tick]);

  return { holidays, window: window_, failed: sameWindow(failedFor, { from, to }), reload: trigger.reload };
}
